import React, { useCallback, useMemo } from 'react'
import { useAppDispatch } from 'hooks/reduxHooks'
import TextLink from 'LuxKit/TextLink'
import { SET_OPTIMIZELY_EXPERIMENT } from 'actions/actionConstants'
import SolidChartIcon from 'LuxKit/Icons/solid/SolidChartIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import SwitchButton from 'LuxKit/Switch/SwitchButton'
import IconButton from 'LuxKit/Button/IconButton'
import Group from 'components/utils/Group'
import { formatOptimizelyTitle } from './ExperimentPanelHelpers'

interface ExperimentItemProps {
  experimentId: string;
  experiment: App.OptimizelyExperimentDetails;
}

export function ExperimentItem({ experimentId, experiment }: ExperimentItemProps) {
  const dispatch = useAppDispatch()

  const handleToggleEnabled = useCallback(() => {
    dispatch({
      type: SET_OPTIMIZELY_EXPERIMENT,
      flagKey: experimentId,
      value: {
        variationKey: !experiment.enabled ? 'on' : 'off',
        enabled: !experiment.enabled,
      },
    })
  }, [experimentId, experiment.enabled, dispatch])

  const experimentTitle = useMemo(() => formatOptimizelyTitle(experimentId), [experimentId])
  const reportAvailable = !(experiment.campaignId === undefined || experiment.experimentId === undefined)

  return (
    <Group direction="vertical" gap={20}>
      <Group direction="vertical" gap={4}>
        <TextLink
          underline={false}
          href={`https://app.optimizely.com/v2/projects/24799170005/flags/manage/${experimentId}`}
          target="_blank"
          weight="regular"
        >
          {experimentTitle}
        </TextLink>
        <BodyText variant="small" colour="neutral-three">
          {experimentId}
        </BodyText>
      </Group>
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
        <Group direction="horizontal" gap={12} verticalAlign="center">
          <BodyText variant="medium" colour="neutral-two">
            <i>Experiment</i>
          </BodyText>
          <Group direction="horizontal" gap={4} verticalAlign="center">
            <BodyText variant="medium" colour="neutral-two">
              Report:
            </BodyText>
            <IconButton
              kind="tertiary"
              size="small"
              href={`https://app.optimizely.com/v2/projects/24799170005/results/${experiment.campaignId}/experiments/${experiment.experimentId}`}
              target="_blank"
              disabled={!reportAvailable}
            >
              <SolidChartIcon />
            </IconButton>
          </Group>
        </Group>

        <SwitchButton
          checked={experiment.enabled}
          onChange={handleToggleEnabled}
        >
          {experiment.enabled ? 'On' : 'Off'}
        </SwitchButton>
      </Group>
    </Group>
  )
}

export default ExperimentItem
